import { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { EmptyState, SectionCard } from "../../mui";
import { listWorklogs, type WorkLogRow } from "../../lib/brain-api";
import { useRowFocus } from "../../agent/useAgentFocus";
import { Referable } from "../../agent/Referable";

const c = brainTokens.color;

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function CustomerLogTabMui({ customerId }: { customerId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  // ?row=<id> / [ref:worklog:…] flashes the row. ponytail: only within the
  // selected month — a ref to another month's row won't switch the filter.
  const { flashId, scrollRef } = useRowFocus("worklog", "row");
  const [month, setMonth] = useState(currentMonth);
  const [rows, setRows] = useState<WorkLogRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listWorklogs({ customer: customerId, month })
      .then((w) => {
        if (!cancelled) setRows(w);
      })
      .catch((err) => {
        if (!cancelled) {
          enqueueSnackbar(err instanceof Error ? err.message : String(err), { variant: "error" });
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, month, enqueueSnackbar]);

  const hours = useMemo(() => rows.reduce((s, r) => s + (Number(r.hours) || 0), 0), [rows]);
  const monthOptions = useMemo(() => {
    const out: string[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return out;
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <TextField
          select
          size="small"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          {monthOptions.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ fontSize: 13, color: c.muted2 }}>
          Hours{" "}
          <Box component="span" sx={{ fontWeight: 700, color: c.ink }}>
            {hours}
          </Box>
          {" · "}
          Rows{" "}
          <Box component="span" sx={{ fontWeight: 700, color: c.ink }}>
            {rows.length}
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading work log…</Box>
      ) : rows.length === 0 ? (
        <EmptyState icon="clock" title="No work log rows">
          Billable activity for {month} will land here via log_activity.
        </EmptyState>
      ) : (
        <SectionCard label={`Work log · ${month}`}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <Box component="thead">
              <Box component="tr" sx={{ color: c.muted2, textAlign: "left" }}>
                {["Person", "Date", "Hours", "Description"].map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{ fontWeight: 500, pb: 1, pr: 1, borderBottom: `1px solid ${c.border}` }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((r) => (
                <Box
                  component="tr"
                  key={r.id}
                  ref={r.id === flashId ? scrollRef : undefined}
                  className={r.id === flashId ? "agent-focus-flash" : undefined}
                >
                  <Box component="td" sx={{ py: 1, pr: 1, borderBottom: `1px solid ${c.borderLight2}` }}>
                    {r.person}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      py: 1,
                      pr: 1,
                      borderBottom: `1px solid ${c.borderLight2}`,
                      fontFamily: brainTokens.font.mono,
                      fontSize: 12,
                    }}
                  >
                    {r.date}
                  </Box>
                  <Box component="td" sx={{ py: 1, pr: 1, borderBottom: `1px solid ${c.borderLight2}` }}>
                    {r.hours}
                  </Box>
                  <Box component="td" sx={{ py: 1, borderBottom: `1px solid ${c.borderLight2}` }}>
                    <Referable
                      label={r.description.length > 40 ? `${r.description.slice(0, 40)}…` : r.description}
                      snapshot={{
                        kind: "worklog",
                        id: r.id,
                        customerId,
                        person: r.person,
                        date: r.date,
                        hours: r.hours,
                        description: r.description,
                      }}
                    >
                      {r.description}
                    </Referable>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
}
